import { Typography, TYPOGRAPHY_WEIGHT } from '../../../components/Typography'
import { Input } from '../../../components/Input'
import { BTN_TXT, PLACEHOLDERS, USERS_PAGE_OPTIONS } from '../../../constants'
import AddAction from './AddAction'
import DeleteAction from './DeleteAction'
import { Table } from '../../../components/Table'
import { columns } from './constants'
import { isTableHasParams } from '../../../helper/common'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  THealthSystemTableRequestData,
  THealthSystemUsersParams
} from '../../HealthSystemUsers/types'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import { useParams } from 'react-router-dom'
import axios, { CancelTokenSource } from 'axios'
import { useDebounce } from '../../../hooks/hooks'
import { setLoading } from '../../../redux/store/common/slice'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import {
  TContractCategoriesUsersFiltersFunc,
  TContractCategoriesUsersData,
  TContractCategoriesUsersFilters
} from './types'
import {
  getContractCategoriesCoUsers,
  getContractCategoriesVendorUsers
} from '../api'
import { useContractCategoryDetailsPopup } from '../Providers/ContractCategoryDetailsPopupProvider'
import { useContractCategoryDetailsContext } from '../Providers/ContractCategoryDetailsContextProvider'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import RadioGroup from '../../../components/RadioGroup/RadioGroup'
import useRouter from '../../../hooks/useRouter'
import { useNavigate } from 'react-router'
import { routes } from '../../../router'
import { contractCategoryDetailsRoute } from '../routes'
import { getUser } from '../../../redux/store/user/getters'
import './styles.scss'
import { Banner } from 'components/Banner/Banner'
import { ContractCategoryStatus } from 'features/Categories/types'

const Users = () => {
  const { addUsersPopup } = useContractCategoryDetailsPopup()
  const dispatch = useDispatch()
  const ccDetailsContext = useContractCategoryDetailsContext()
  const { actions, state } = ccDetailsContext
  const { getCCDetailsAsync } = actions
  const { details, canManageCCUsers, canSeeHealthSystem } = state
  const user = useSelector(getUser)

  const { query } = useRouter()
  const navigate = useNavigate()
  const [activeSubTab, setActiveSubTab] = useState(
    query?.subtab || USERS_PAGE_OPTIONS[0].value
  )

  const isVendorTab = activeSubTab === USERS_PAGE_OPTIONS[1].value

  const [totalNumber, setTotalNumber] = useState(0)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<
    TContractCategoriesUsersData[]
  >([])
  const [searchValue, setSearchValue] = useState('')
  const [tableData, setTableData] = useState<TContractCategoriesUsersData[]>([])
  const [sortParams, setSortParams] = useState<THealthSystemTableRequestData>()
  const [filters, setFilters] = useState<TContractCategoriesUsersFilters>({})
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })

  const { id } = useParams()

  const tableDataTokenRef = useRef<CancelTokenSource>()

  const debouncedSearch = useDebounce(searchValue, 500)

  const handleTabs = (e) => {
    dispatch(setLoading(true))
    resetTableData()
    setActiveSubTab(e)
    navigate(
      `${routes.contracts}${routes.contractCategories}/${query.id}/${contractCategoryDetailsRoute.users}/${e}`
    )
  }

  useEffect(() => {
    getTableData({ ...sortParams, ...filters, page: { ...DEFAULT_PAGE } })
    setPageInfo({ ...DEFAULT_PAGE })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, isVendorTab])

  const resetTableData = () => {
    setPageInfo({ ...DEFAULT_PAGE })
    setTableData([])
    setSearchValue('')
    setSelectedRowKeys([])
    setFilters({})
    setSortParams({})
    setTotalNumber(0)
  }

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: THealthSystemTableRequestData) => {
    dispatch(setLoading(true))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: THealthSystemUsersParams = { ...filters }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    if (searchValue) {
      params.search = searchValue
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    const apiRequest = isVendorTab
      ? getContractCategoriesVendorUsers
      : getContractCategoriesCoUsers
    apiRequest(id as string, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            key: i.uuid
          }))
        )
        setSelectedRowKeys([])
        setSelectedRows([])
        setTotalNumber(resp.data.count)
        getCCDetailsAsync()
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const applyFilters: TContractCategoriesUsersFiltersFunc = (
    field,
    appliedFilters
  ) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }
  const handleSelectRow = (selectedRowKeys, selectedRow) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRow)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const isCanEditUsers = useMemo(
    () => canManageCCUsers && (isVendorTab ? user.vendor : !user.vendor),
    [canManageCCUsers, isVendorTab, user.vendor]
  )

  const isBannerVisible = useMemo(() => {
    return (
      !details.is_manual &&
      activeSubTab === USERS_PAGE_OPTIONS[1].value &&
      (details.status === ContractCategoryStatus.CREATED ||
        details.status === ContractCategoryStatus.RFP)
    )
  }, [details, activeSubTab])

  return (
    <div className="table-wrapper">
      <div className="mb-24">
        <RadioGroup.Buttons
          options={USERS_PAGE_OPTIONS}
          defaultValue={activeSubTab}
          onChange={handleTabs}
        />
      </div>
      {isBannerVisible && (
        <Banner.Info
          text={
            'RFP is ongoing. Vendors will be displayed once contract is awarded.'
          }
          className="mb-32"
        />
      )}
      <div className="table-wrapper__header">
        {!!selectedRowKeys.length ? (
          <Typography.Body1
            className="table-wrapper__select-title"
            weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
          >
            Selected {selectedRowKeys.length} of {totalNumber}
          </Typography.Body1>
        ) : (
          <Input
            className="allow-clear"
            propsInput={{
              allowClear: { clearIcon: <Close16 /> },
              className: 'rfp-hs-users_search',
              placeholder: PLACEHOLDERS.SEARCH,
              prefix: <Search16 />,
              value: searchValue,
              onChange: (e) => setSearchValue(e.target.value)
            }}
          />
        )}
        <div className="table-wrapper__header_buttons">
          {isCanEditUsers && (
            <>
              <AddAction
                updateAssignedValues={() =>
                  getTableData({ ...sortParams, ...filters })
                }
                selected={selectedRowKeys}
                isVendorTab={isVendorTab}
                canSeeHealthSystem={canSeeHealthSystem}
              />
              <DeleteAction
                updateAssignedValues={() =>
                  getTableData({ ...sortParams, ...filters })
                }
                selected={selectedRowKeys}
                selectedUsers={selectedRows}
              />
            </>
          )}
        </div>
      </div>
      <Table<TContractCategoriesUsersData>
        dataSource={tableData}
        columns={columns({
          applyFilters,
          filters,
          ccId: id || '',
          isVendorTab,
          isVendorUser: !!user?.vendor,
          isAutoCreated: !details.is_manual,
          canSeeHealthSystem
        })}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        className="rfp-vendors-data-list-table"
        onChangePage={handleChangePageSize}
        onChange={handleTableChange}
        hasSearchOrFilters={isTableHasParams<TContractCategoriesUsersFilters>(
          debouncedSearch,
          filters
        )}
        createButton={
          isCanEditUsers
            ? {
                buttonText: BTN_TXT.ADD_USER,
                availability: true,
                action: addUsersPopup.actions.open
              }
            : undefined
        }
        rowSelection={
          isCanEditUsers
            ? {
                selectedRowKeys,
                onChange: handleSelectRow,
                getCheckboxProps: (record) => ({
                  ...record,
                  disabled:
                    record.uuid === details.owner?.uuid ||
                    ('can_be_removed' in record && !record.can_be_removed)
                }),
                renderCell: (_checked, record, _, node) => {
                  if (
                    record.uuid === details.owner?.uuid ||
                    ('can_be_removed' in record && !record.can_be_removed)
                  ) {
                    return (
                      <Tooltip
                        title={'User cannot be removed from the list'}
                        overlayInnerStyle={{
                          display: 'block',
                          width: 264
                        }}
                        placement="topLeft"
                      >
                        {node}
                      </Tooltip>
                    )
                  }
                  return node
                }
              }
            : undefined
        }
      />
    </div>
  )
}

export default Users
